/**
 * Folha: 60mm × 40mm | Margem: 2mm | Área útil: 56mm × 36mm
 *
 * Tablet (App-Tech 421B): preview em tela cheia + botão Imprimir
 * (iframe 0x0 e window.open com noopener quebram no Android/Chrome).
 */

export const BADGE_PRINT_STYLES = `
  @page {
    size: 60mm 40mm;
    margin: 2mm;
  }

  * {
    box-sizing: border-box;
  }

  .badge {
    width: 56mm !important;
    height: 36mm !important;
    max-width: 56mm !important;
    max-height: 36mm !important;
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    gap: 1.5mm !important;
    padding: 1mm 1.5mm !important;
    margin: 0 !important;
    overflow: hidden !important;
    background: #fff !important;
  }

  .badge-qr {
    flex-shrink: 0 !important;
    width: 18mm !important;
    height: 18mm !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  .badge-qr svg {
    width: 18mm !important;
    height: 18mm !important;
    max-width: 18mm !important;
    max-height: 18mm !important;
  }

  .badge-info {
    flex: 1 !important;
    min-width: 0 !important;
    max-width: 34mm !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: center !important;
    gap: 0.8mm !important;
    overflow: hidden !important;
  }

  .badge-name {
    font-size: 7pt !important;
    font-weight: 700 !important;
    line-height: 1.1 !important;
    color: #27272a !important;
    max-width: 34mm !important;
    max-height: 16mm !important;
    margin: 0 !important;
    display: -webkit-box !important;
    -webkit-box-orient: vertical !important;
    -webkit-line-clamp: 2 !important;
    overflow: hidden !important;
    word-break: break-word !important;
  }

  .badge-institution {
    font-size: 5.5pt !important;
    font-weight: 600 !important;
    line-height: 1.1 !important;
    color: #52525b !important;
    max-width: 34mm !important;
    max-height: 12mm !important;
    margin: 0 !important;
    display: -webkit-box !important;
    -webkit-box-orient: vertical !important;
    -webkit-line-clamp: 2 !important;
    overflow: hidden !important;
    word-break: break-word !important;
  }

  .print-page {
    width: 56mm;
    height: 36mm;
    max-width: 56mm;
    max-height: 36mm;
    overflow: hidden;
    background: #fff;
    page-break-after: always;
    break-after: page;
    page-break-inside: avoid;
    break-inside: avoid;
  }

  .print-page:last-child {
    page-break-after: auto !important;
    break-after: auto !important;
  }
`;

const OVERLAY_ID = "badge-print-overlay";
const STYLE_ID = "badge-print-overlay-style";

function wrapBadgesForPrint(contentHtml: string): string {
  if (contentHtml.includes("print-page")) {
    return contentHtml;
  }

  const container = window.document.createElement("div");
  container.innerHTML = contentHtml;
  const badges = Array.from(container.querySelectorAll(".badge"));

  if (badges.length === 0) {
    return `<div class="print-page">${contentHtml}</div>`;
  }

  return badges
    .map((badge) => `<div class="print-page">${badge.outerHTML}</div>`)
    .join("");
}

function countPrintPages(pagesHtml: string): number {
  const match = pagesHtml.match(/print-page/g);
  return match ? match.length : 1;
}

function removePrintOverlay() {
  window.document.getElementById(OVERLAY_ID)?.remove();
  window.document.getElementById(STYLE_ID)?.remove();
  window.document.body.style.overflow = "";
}

/**
 * Abre preview em tela cheia e imprime com gesto do usuário (obrigatório no tablet).
 */
export function openBadgePrintWindow(contentHtml: string): boolean {
  removePrintOverlay();

  const pagesHtml = wrapBadgesForPrint(contentHtml);
  const total = countPrintPages(pagesHtml);

  const style = window.document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
    ${BADGE_PRINT_STYLES}

    #${OVERLAY_ID} {
      position: fixed;
      inset: 0;
      z-index: 99999;
      display: flex;
      flex-direction: column;
      background: #0f172a;
      color: #fff;
      font-family: Arial, Helvetica, sans-serif;
    }

    #${OVERLAY_ID} .badge-print-toolbar {
      flex-shrink: 0;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 14px 16px;
      background: #015832;
      box-shadow: 0 2px 12px rgba(0,0,0,.25);
    }

    #${OVERLAY_ID} .badge-print-toolbar h2 {
      margin: 0;
      font-size: 16px;
      font-weight: 700;
    }

    #${OVERLAY_ID} .badge-print-toolbar p {
      margin: 4px 0 0;
      font-size: 12px;
      opacity: .85;
    }

    #${OVERLAY_ID} .badge-print-actions {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }

    #${OVERLAY_ID} .badge-print-actions button {
      min-height: 44px;
      padding: 0 18px;
      border: 0;
      border-radius: 10px;
      font-size: 15px;
      font-weight: 700;
      cursor: pointer;
    }

    #${OVERLAY_ID} .btn-print {
      background: #fff;
      color: #015832;
    }

    #${OVERLAY_ID} .btn-close {
      background: rgba(255,255,255,.15);
      color: #fff;
    }

    #${OVERLAY_ID} .badge-print-preview {
      flex: 1;
      overflow: auto;
      -webkit-overflow-scrolling: touch;
      padding: 20px 16px 32px;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;
      background: #1e293b;
    }

    #${OVERLAY_ID} .badge-print-sheet {
      background: #fff;
      border-radius: 4px;
      box-shadow: 0 8px 24px rgba(0,0,0,.35);
      padding: 8px;
    }

    #${OVERLAY_ID} .badge-print-caption {
      text-align: center;
      font-size: 11px;
      color: #94a3b8;
      margin-top: 6px;
    }

    @media print {
      /* Esconde o app e a toolbar; imprime só as páginas do crachá */
      body > *:not(#${OVERLAY_ID}) {
        display: none !important;
      }

      #${OVERLAY_ID} {
        position: static !important;
        inset: auto !important;
        background: #fff !important;
        color: #000 !important;
        display: block !important;
      }

      #${OVERLAY_ID} .badge-print-toolbar,
      #${OVERLAY_ID} .badge-print-caption {
        display: none !important;
      }

      #${OVERLAY_ID} .badge-print-preview {
        display: block !important;
        overflow: visible !important;
        padding: 0 !important;
        background: #fff !important;
        gap: 0 !important;
      }

      #${OVERLAY_ID} .badge-print-sheet {
        box-shadow: none !important;
        border-radius: 0 !important;
        padding: 0 !important;
        margin: 0 !important;
      }

      #${OVERLAY_ID} .print-page {
        page-break-after: always;
        break-after: page;
        page-break-inside: avoid;
      }

      #${OVERLAY_ID} .print-page:last-child {
        page-break-after: auto;
        break-after: auto;
      }
    }
  `;
  window.document.head.appendChild(style);

  const overlay = window.document.createElement("div");
  overlay.id = OVERLAY_ID;
  overlay.innerHTML = `
    <div class="badge-print-toolbar">
      <div>
        <h2>Preview de impressão</h2>
        <p>${total} crachá(s) · folha 60×40 mm</p>
      </div>
      <div class="badge-print-actions">
        <button type="button" class="btn-close" data-action="close">Fechar</button>
        <button type="button" class="btn-print" data-action="print">Imprimir</button>
      </div>
    </div>
    <div class="badge-print-preview" data-preview></div>
  `;

  const preview = overlay.querySelector("[data-preview]") as HTMLElement;
  const temp = window.document.createElement("div");
  temp.innerHTML = pagesHtml;
  const pages = Array.from(temp.querySelectorAll(".print-page"));

  pages.forEach((page, index) => {
    const sheet = window.document.createElement("div");
    sheet.className = "badge-print-sheet";
    sheet.appendChild(page);
    const caption = window.document.createElement("div");
    caption.className = "badge-print-caption";
    caption.textContent = `Página ${index + 1} de ${total}`;
    sheet.appendChild(caption);
    preview.appendChild(sheet);
  });

  // Se não achou .print-page, injeta o HTML cru
  if (pages.length === 0) {
    const sheet = window.document.createElement("div");
    sheet.className = "badge-print-sheet";
    sheet.innerHTML = pagesHtml;
    preview.appendChild(sheet);
  }

  overlay.addEventListener("click", (event) => {
    const target = event.target as HTMLElement | null;
    const action = target?.closest("button")?.getAttribute("data-action");
    if (action === "close") {
      removePrintOverlay();
      return;
    }
    if (action === "print") {
      // Precisa ser no clique do usuário (tablet bloqueia print automático)
      window.print();
    }
  });

  window.document.body.style.overflow = "hidden";
  window.document.body.appendChild(overlay);

  // Após imprimir (ou cancelar), mantém o preview aberto para reimprimir se quiser
  return true;
}

/** Envolve HTML de um crachá em página de impressão (uso no lote) */
export function wrapBadgeAsPrintPage(badgeHtml: string): string {
  return `<div class="print-page">${badgeHtml}</div>`;
}
